import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";

import { PlatoChip } from "@ats/src/components/shared/PlatoMark";
import BetaTag from "@ats/src/components/shared/BetaTag";
import { FitStars, fitBand, fitTagVariant } from "./FitIndicator";
import PlatoScoreTag from "./PlatoScoreTag";

export interface PlatoGeneratedReviewCalloutProps {
  headline: string;
  roleFit: string;
  scorePct: number;
  generatedAgo: string;
  onClick?: () => void;
}

const PlatoGeneratedReviewCallout: React.FunctionComponent<PlatoGeneratedReviewCalloutProps> = ({
  headline,
  roleFit,
  scorePct,
  generatedAgo,
  onClick,
}) => {
  const band = fitBand(scorePct);
  return (
    <Styled.Callout>
      <Styled.Bar />
      <Styled.Body>
        <Styled.Header>
          <Styled.Attribution>
            <PlatoChip size={24} />
            <span>
              Generated by Plato <BetaTag size="small" variant="filledDarkOnly" /> <Styled.Sep>·</Styled.Sep> {generatedAgo}
            </span>
          </Styled.Attribution>
          <Styled.Cta onClick={onClick}>
            See full review
            <Icon name="chevron-right" />
          </Styled.Cta>
        </Styled.Header>

        <Styled.Headline>{headline}</Styled.Headline>
        <Styled.RoleFit>{roleFit}</Styled.RoleFit>

        <Styled.Score>
          <PlatoScoreTag variant={fitTagVariant(band.key)} size={20}>
            {band.label}
          </PlatoScoreTag>
          <FitStars value={band.fill} label={band.label} size={16} gap={2} />
        </Styled.Score>
      </Styled.Body>
    </Styled.Callout>
  );
};

export default PlatoGeneratedReviewCallout;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Callout = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout;
    position: relative;
    display: flex;
    ${[t.py(3), t.px(4), t.rounded.md]}
    margin: ${t.spacing[4]} 0;
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 100%;
      left: 1.75rem;
      width: 4px;
      margin-left: -2px;
      height: ${t.spacing[6]};
      background-color: ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    }
  `;
});

Styled.Bar = styled.span`
  label: PlatoGeneratedReviewCallout_Bar;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
  border-radius: 0.4375rem 0 0 0.4375rem;
`;

Styled.Body = styled.div`
  label: PlatoGeneratedReviewCallout_Body;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

Styled.Header = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Header;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${t.spacing[2]};
    margin-bottom: ${t.spacing[3]};
  `;
});

Styled.Attribution = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Attribution;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    min-width: 0;
    ${t.text.xs}
    font-size: 0.75rem;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;
});

Styled.Sep = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Sep;
    margin: 0 ${t.spacing[1]};
    color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
  `;
});

Styled.Cta = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Cta;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[1]};
    flex-shrink: 0;
    ${[t.text.sm, t.text.medium]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
    svg {
      width: 15px;
      height: 15px;
    }
  `;
});

Styled.Headline = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Headline;
    ${[t.text.sm, t.text.bold]}
    line-height: 1.3;
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    margin-bottom: ${t.spacing[2]};
  `;
});

Styled.RoleFit = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_RoleFit;
    ${t.text.sm}
    line-height: 1.45;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: ${t.spacing[3]};
  `;
});

Styled.Score = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoGeneratedReviewCallout_Score;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
  `;
});

